// noinspection DuplicatedCode

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { concat } from 'lodash';
import moment from 'moment/moment';

import { ApplicationMethodTypeId, CalculationMethods } from 'src/app/pages/marketing/discounts/wizard-refactor/models';
import { fetchData, postData } from 'src/app/queries';

import type {
  Discount,
  DiscountListItem,
  DiscountMenuCard,
  DiscountMenuCardImage,
} from 'src/app/pages/marketing/discounts/wizard-refactor/models';
import type { Discount as DiscountV2 } from 'src/app/pages/marketing/discounts/wizard-refactor/wizard/types';
import type { Error } from 'src/gql/graphql';

type DiscountRank = {
  DiscountId: number;
  Rank: number;
};
type UpdateDiscountMenuRanksRequest = {
  DiscountIdsToRanks: DiscountRank[];
};
type UpdateDiscountMenuCardResponse = {
  DiscountId: number;
  DiscountMenuDisplayId: number;
};
type UploadDiscountMenuImageRequest = {
  Base64Image: string;
};
type DiscountIdRequest = {
  DiscountId: number;
};
type IdRequest = {
  Id: number;
};

export type CopyDiscountRequest = IdRequest;
export type DeactivateDiscountRequest = IdRequest & {
  IsDeleted: true;
};
export type GetDiscountMenuCardRequest = DiscountIdRequest;
export type GetDiscountByIdRequest = DiscountIdRequest;
export type UpdateDiscountRequest = {
  Discount: Discount;
};
export type UpdateDiscountRequestV2 = {
  Discount: DiscountV2;
};
export type UpdateDiscountMenuCardRequest = {
  DiscountMenuDisplay: DiscountMenuCard;
};

export const discountQueryKeys = {
  // All discount queries
  all: ['discount'] as const,
  // Hierarchy for lists of discounts
  lists: () => [...discountQueryKeys.all, 'list'] as const,
  // All DiscountListItems
  listAll: () => [...discountQueryKeys.lists(), 'all'] as const,
  // Hierarchy for individual discounts
  details: () => [...discountQueryKeys.all, 'detail'] as const,
  // An individual discount's details by discountId
  detail: (id: number) => [...discountQueryKeys.details(), id] as const,
  // Hierarchy for discount menu cards
  menuCard: () => [...discountQueryKeys.all, 'menuCard'] as const,
  // Hierarchy for individual discount menu card details
  menuCardDetails: () => [...discountQueryKeys.menuCard(), 'detail'] as const,
  // Hierarchy for individual discount menu card details stored by discountId
  menuCardDetailsByDiscountId: () => [...discountQueryKeys.menuCardDetails(), 'discountId'] as const,
  // An individual discount menu card's details by discountId
  menuCardDetailByDiscountId: (discountId: number) =>
    [...discountQueryKeys.menuCardDetailsByDiscountId(), discountId] as const,
};

export const GET_DISCOUNTS = 'api/v2/discount/get-discount';

export function useDiscounts(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: discountQueryKeys.listAll(),
    queryFn: () => fetchData<DiscountListItem[]>(GET_DISCOUNTS),
    enabled: options?.enabled,
  });
}

export function useDiscount(discountId: number) {
  return useQuery({
    enabled: discountId !== 0,
    queryKey: discountQueryKeys.detail(discountId),
    queryFn: async () => {
      const response = await getDiscountById({ DiscountId: discountId });
      // error state for this endpoint is a 200 response code with Result: true but Data: null
      // TODO - fix that endpoint to return (at a minimum) Result: false when id is invalid
      if (response === null) {
        return null;
      }
      formatDiscountResponse(response);
      return response;
    },
  });
}

export function useDiscountMenuCard(discountId: number) {
  const result = useQuery<DiscountMenuCard, Error>({
    queryKey: discountQueryKeys.menuCardDetailByDiscountId(discountId),
    queryFn: async () => getDiscountMenuCard({ DiscountId: discountId }),
  });
  return result;
}

export function useCopyDiscount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: copyDiscount,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: discountQueryKeys.listAll() });
    },
  });
}

export function useDeactivateDiscount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deactivateDiscount,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: discountQueryKeys.listAll() });
    },
  });
}

export function useUpdateDiscountDetail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateDiscountDetail,
    onSuccess: (newDiscountId) => {
      void queryClient.invalidateQueries({ queryKey: discountQueryKeys.detail(newDiscountId) });
      void queryClient.invalidateQueries({ queryKey: discountQueryKeys.listAll() });
    },
  });
}

export function useUpdateDiscountDetailV2() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateDiscountDetailV2,
    onSuccess: (newDiscountId) => {
      void queryClient.invalidateQueries({ queryKey: discountQueryKeys.detail(newDiscountId) });
      void queryClient.invalidateQueries({ queryKey: discountQueryKeys.listAll() });
    },
  });
}

export function useUpdateDiscountMenuCard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateDiscountMenuCard,
    onSuccess: (updatedMenuCard) => {
      void queryClient.invalidateQueries(discountQueryKeys.menuCardDetailByDiscountId(updatedMenuCard.DiscountId));
    },
  });
}

export function useUploadDiscountMenuImage() {
  return useMutation({
    mutationFn: uploadDiscountMenuImage,
  });
}

export function useUpdateDiscountMenuRanks() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateDiscountMenuRanks,
    // ** Optimistically update query cache **
    onMutate: async (newMenuRanks: Map<number, number>) => {
      // Cancel any in-progress refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries(discountQueryKeys.listAll());
      // Get discounts before updates
      const previousDiscounts = queryClient.getQueryData(discountQueryKeys.listAll()) as DiscountListItem[];
      // ** Update menu ranks **
      const discountIdsToUpdate = Array.from(newMenuRanks.entries());
      const discountsWithUpdate = discountIdsToUpdate.map(([id, rank]: [number, number]) => {
        const disc = { ...previousDiscounts.find((val: DiscountListItem) => val.Id === id) };
        if (disc) {
          disc.MenuDisplayRank = rank;
        }
        return disc;
      });
      // Get all the discounts we didn't update
      const notUpdatedDiscounts = previousDiscounts.filter((val) => !newMenuRanks.has(val.Id));
      // Concatenate the updated discounts with the ones that were not updated
      const newDiscounts = concat(discountsWithUpdate, notUpdatedDiscounts) as DiscountListItem[];
      // Update the query cache
      queryClient.setQueryData(discountQueryKeys.listAll(), newDiscounts);
      // Return context with previous and new discounts so we can revert in case of error
      return { previousDiscounts, newDiscounts };
    },
    onError: (_err, _newMenuRanks: Map<number, number>, context) => {
      // Query had an error, so revert our optimistic update
      if (context?.previousDiscounts) {
        queryClient.setQueryData(discountQueryKeys.listAll(), context.previousDiscounts);
      }
    },
    retry: 3,
    onSettled: () => {
      void queryClient.invalidateQueries(discountQueryKeys.lists());
      void queryClient.invalidateQueries(discountQueryKeys.details());
    },
    // TODO: uncomment once updated to react-query v5.
    //   This makes this mutation run serially instead of in parallel, if there are multiple calls to it.
    //   In cases of slow network this will be useful to avoid race conditions.
    // scope: {
    //   id: 'menuRanks',
    // },
  });
}

// ** Fetch helper functions **
const getDiscountById = async (payload: GetDiscountByIdRequest) =>
  fetchData<Discount>('api/v2/discount/get-discount-by-id', payload);
const getDiscountMenuCard = async (payload: GetDiscountMenuCardRequest) =>
  fetchData<DiscountMenuCard>('api/v2/discount/get-discount-menu-display', payload);

// ** Mutation helper functions **
const copyDiscount = async (payload: CopyDiscountRequest) =>
  postData<CopyDiscountRequest, Discount>({
    payload,
    endpoint: 'api/v2/discount/copy-discount',
  });
const deactivateDiscount = async (payload: DeactivateDiscountRequest) =>
  postData<DeactivateDiscountRequest, Record<string, never>>({
    payload,
    endpoint: 'api/v2/discount/set-delete-discount',
  });
const updateDiscountMenuRanks = async (payload: Map<number, number>) =>
  postData<UpdateDiscountMenuRanksRequest, Record<string, never>>({
    payload: discountRankMapToRequestPayload(payload),
    endpoint: 'api/v2/discount/update-discounts-ranks',
  });
const updateDiscountDetail = async (payload: Discount) =>
  postData<UpdateDiscountRequest, number>({
    payload: {
      Discount: payload,
    },
    endpoint: 'api/v2/discount/update-discount-item',
  });
const updateDiscountDetailV2 = async (payload: DiscountV2) =>
  postData<UpdateDiscountRequestV2, number>({
    payload: {
      Discount: payload,
    },
    endpoint: 'api/v2/discount/update-discount-item',
  });

const updateDiscountMenuCard = async (payload: UpdateDiscountMenuCardRequest) =>
  postData<UpdateDiscountMenuCardRequest, UpdateDiscountMenuCardResponse>({
    payload: {
      DiscountMenuDisplay: payload.DiscountMenuDisplay,
    },
    endpoint: 'api/v2/discount/update-discount-menu-card',
  });

const uploadDiscountMenuImage = async (payload: UploadDiscountMenuImageRequest) =>
  postData<UploadDiscountMenuImageRequest, DiscountMenuCardImage>({
    payload,
    endpoint: 'api/v2/discount/upload-discount-menu-image',
  });

// ** Data manipulation functions **
const formatDiscountResponse = (response: Discount) => {
  response.ValidDateFrom = moment(response.ValidDateFrom).format('MM/DD/YYYY hh:mm A') || '';
  response.ValidDateTo = moment(response.ValidDateTo).format('MM/DD/YYYY hh:mm A') || '';
  response.StartTime = response.StartTime ? moment(response.StartTime, 'LT').format('h:mm a') : undefined;
  response.EndTime = response.EndTime ? moment(response.EndTime, 'LT').format('h:mm a') : undefined;
  response.Reward.DiscountValue = [CalculationMethods.PercentOff, CalculationMethods.PriceToCostPlusPercent].includes(
    response.Reward.CalculationMethodId
  )
    ? response.Reward.DiscountValue * 100
    : response.Reward.DiscountValue;

  // Manual discounts cannot have constraints; redundantly enforce that here
  if (ApplicationMethodTypeId.Manually === response.ApplicationMethodId) {
    response.Constraints = [];
  }
};

const discountRankMapToRequestPayload = (rankMap: Map<number, number>): UpdateDiscountMenuRanksRequest => {
  const ranks: DiscountRank[] = [];
  for (const [key, value] of rankMap) {
    ranks.push({ DiscountId: Number(key), Rank: Number(value) });
  }
  return { DiscountIdsToRanks: ranks };
};
